import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ApiError, type components } from '@awj/api-client';
import { Button, Dialog, Skeleton, useToast } from '@awj/ui';

import { DateTimeField } from '~/components/DateTimeField.tsx';
import { EmptyState } from '~/components/EmptyState.tsx';
import { FormPage } from '~/components/FormPage.tsx';
import { FieldHead } from '~/components/InfoHint.tsx';
import { PageTitle } from '~/components/PageTitle.tsx';
import { Select } from '~/components/Select.tsx';
import { VisibilitySwitch } from '~/components/content/fields.tsx';
import { useI18n } from '~/i18n/index.tsx';
import { useCoupons, useDeleteCoupon, useSaveCoupon } from '~/lib/coupon-queries.ts';
import { useUnsavedChanges } from '~/lib/unsaved-changes.tsx';

type Coupon = components['schemas']['CouponResponse'];

interface Draft {
  code: string;
  type: 'Percentage' | 'FixedAmount';
  value: string;
  minimumOrderAmount: string;
  maximumDiscountAmount: string;
  startsAt: string;
  expiresAt: string;
  usageLimit: string;
  isActive: boolean;
}

const EMPTY: Draft = {
  code: '',
  type: 'Percentage',
  value: '',
  minimumOrderAmount: '',
  maximumDiscountAmount: '',
  startsAt: '',
  expiresAt: '',
  usageLimit: '',
  isActive: true,
};

const LIST = '/coupons';

/** The form's fields want `YYYY-MM-DDTHH:mm`, with no zone and no seconds. */
function toLocalInput(value: string | null | undefined): string {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  const pad = (part: number) => String(part).padStart(2, '0');

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

const from = (coupon: Coupon): Draft => ({
  code: coupon.code,
  type: coupon.type === 'FixedAmount' ? 'FixedAmount' : 'Percentage',
  value: String(coupon.value),
  minimumOrderAmount: coupon.minimumOrderAmount != null ? String(coupon.minimumOrderAmount) : '',
  maximumDiscountAmount: coupon.maximumDiscountAmount != null ? String(coupon.maximumDiscountAmount) : '',
  startsAt: toLocalInput(coupon.startsAt),
  expiresAt: toLocalInput(coupon.expiresAt),
  usageLimit: coupon.usageLimit != null ? String(coupon.usageLimit) : '',
  isActive: coupon.isActive,
});

const amountOrNull = (value: string) => (value.trim() === '' ? null : Number(value));

/**
 * One discount code.
 *
 * What it takes off, then what it asks of the basket, then when and how often it may be used.
 * Every condition is optional: a bare code with a percentage is a complete coupon.
 */
export function CouponEditorPage() {
  const { t } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const { id } = useParams();

  const coupons = useCoupons();
  const save = useSaveCoupon();
  const remove = useDeleteCoupon();

  const isNew = id === undefined;
  const editing = isNew ? undefined : (coupons.data ?? []).find((coupon) => String(coupon.id) === id);

  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [baseline, setBaseline] = useState<Draft>(EMPTY);
  const unsaved = useUnsavedChanges(JSON.stringify(draft) !== JSON.stringify(baseline));
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (editing) {
      setDraft(from(editing));
      setBaseline(from(editing));
    }
  }, [editing]);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((current) => ({ ...current, [key]: value }));

  const back = () => navigate(LIST);

  /** Checked here so the reason arrives in the reader's language, before a round trip. */
  function problem(): string | null {
    const value = Number(draft.value);

    if (!/^[A-Za-z0-9_-]+$/.test(draft.code.trim())) {
      return t('coupons.codeInvalid');
    }

    if (!(value > 0)) {
      return t('coupons.valueInvalid');
    }

    if (draft.type === 'Percentage' && value > 100) {
      return t('coupons.percentTooHigh');
    }

    if (draft.startsAt && draft.expiresAt && draft.expiresAt <= draft.startsAt) {
      return t('coupons.endsBeforeStart');
    }

    return null;
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();

    const found = problem();

    if (found) {
      setError(found);
      return;
    }

    try {
      await save.mutateAsync({
        id: editing?.id ?? null,
        body: {
          code: draft.code.trim(),
          type: draft.type,
          value: Number(draft.value),
          minimumOrderAmount: amountOrNull(draft.minimumOrderAmount),
          maximumDiscountAmount: draft.type === 'Percentage' ? amountOrNull(draft.maximumDiscountAmount) : null,
          // Sent as instants: the fields are local time, and the API stores the moment.
          startsAt: draft.startsAt ? new Date(draft.startsAt).toISOString() : null,
          expiresAt: draft.expiresAt ? new Date(draft.expiresAt).toISOString() : null,
          usageLimit: draft.usageLimit.trim() === '' ? null : Math.trunc(Number(draft.usageLimit)),
          isActive: draft.isActive,
        },
      });

      toast.show({ title: t('coupons.saved'), tone: 'success' });
      unsaved.release();
      back();
    } catch (caught) {
      setError(
        caught instanceof ApiError && caught.is('coupon.code_taken')
          ? t('coupons.codeTaken')
          : caught instanceof ApiError
            ? (caught.problem.detail ?? t('common.somethingWentWrong'))
            : t('common.somethingWentWrong'),
      );
    }
  }

  async function deleteCoupon() {
    if (!editing) {
      return;
    }

    try {
      await remove.mutateAsync(editing.id);
      toast.show({ title: t('coupons.deleted'), tone: 'success' });
      unsaved.release();
      back();
    } catch (caught) {
      setConfirmDelete(false);
      toast.show({
        title: t('catalog.couldNotDelete'),
        message: caught instanceof ApiError && caught.is('coupon.in_use') ? t('coupons.inUse') : undefined,
        tone: 'danger',
      });
    }
  }

  if (!isNew && coupons.isPending) {
    return <Skeleton height="520px" />;
  }

  if (!isNew && !editing) {
    return (
      <>
        <PageTitle title={t('coupons.edit')} back={LIST} />
        <EmptyState
          icon="alert"
          title={coupons.isError ? t('common.somethingWentWrong') : t('coupons.notFound')}
          action={
            <Button variant="secondary" onClick={coupons.isError ? () => coupons.refetch() : back}>
              {coupons.isError ? t('common.retry') : t('nav.coupons')}
            </Button>
          }
          tall
        />
      </>
    );
  }

  const percentage = draft.type === 'Percentage';

  return (
    <FormPage
      icon="coupons"
      title={isNew ? t('coupons.new') : t('coupons.edit')}
      subtitle={t('coupons.editorLede')}
      onBack={back}
      onSubmit={submit}
      submitLabel={t('common.save')}
      submitting={save.isPending}
    >
      <div className="page-editor">
        <section className="card part-card" aria-labelledby="coupon-discount">
          <header className="part-card__head">
            <h2 className="part-card__title" id="coupon-discount">
              {t('coupons.discount')}
            </h2>
          </header>

          <div className="part-fields">
            <div className="field">
              <FieldHead htmlFor="coupon-code" label={t('coupons.code')} hint={t('coupons.codeHint')} />
              <input
                id="coupon-code"
                className="awj-input mono"
                dir="ltr"
                maxLength={40}
                autoComplete="off"
                value={draft.code}
                onChange={(event) => set('code', event.target.value.toUpperCase())}
              />
            </div>

            <div className="part-fields__pair">
              <div className="field">
                <label htmlFor="coupon-type">{t('coupons.type')}</label>
                <Select
                  id="coupon-type"
                  value={draft.type}
                  onChange={(type) => set('type', type === 'FixedAmount' ? 'FixedAmount' : 'Percentage')}
                  options={[
                    { value: 'Percentage', label: t('coupons.typePercentage') },
                    { value: 'FixedAmount', label: t('coupons.typeFixed') },
                  ]}
                />
              </div>

              <div className="field">
                <label htmlFor="coupon-value">{percentage ? t('coupons.percentOff') : t('coupons.amountOff')}</label>
                <input
                  id="coupon-value"
                  className="awj-input"
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step={percentage ? '1' : '0.01'}
                  value={draft.value}
                  onChange={(event) => set('value', event.target.value)}
                />
              </div>
            </div>

            <div className="part-fields__pair">
              <div className="field">
                <FieldHead
                  htmlFor="coupon-minimum"
                  label={t('coupons.minimum')}
                  hint={t('coupons.minimumHint')}
                />
                <input
                  id="coupon-minimum"
                  className="awj-input"
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="0.01"
                  value={draft.minimumOrderAmount}
                  onChange={(event) => set('minimumOrderAmount', event.target.value)}
                />
              </div>

              {/* A cap only means something on a percentage; on a fixed amount the amount is the cap. */}
              {percentage ? (
                <div className="field">
                  <FieldHead htmlFor="coupon-cap" label={t('coupons.cap')} hint={t('coupons.capHint')} />
                  <input
                    id="coupon-cap"
                    className="awj-input"
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="0.01"
                    value={draft.maximumDiscountAmount}
                    onChange={(event) => set('maximumDiscountAmount', event.target.value)}
                  />
                </div>
              ) : null}
            </div>
          </div>
        </section>

        <section className="card part-card" aria-labelledby="coupon-limits">
          <header className="part-card__head">
            <h2 className="part-card__title" id="coupon-limits">
              {t('coupons.limits')}
            </h2>
          </header>

          <div className="part-fields">
            <div className="field">
              <label htmlFor="coupon-starts">{t('content.startsAt')}</label>
              <DateTimeField id="coupon-starts" value={draft.startsAt} onChange={(value) => set('startsAt', value)} />
            </div>

            <div className="field">
              <label htmlFor="coupon-ends">{t('content.endsAt')}</label>
              <DateTimeField
                id="coupon-ends"
                value={draft.expiresAt}
                min={draft.startsAt || undefined}
                onChange={(value) => set('expiresAt', value)}
              />
            </div>

            <div className="field">
              <FieldHead htmlFor="coupon-limit" label={t('coupons.usageLimit')} hint={t('coupons.usageLimitHint')} />
              <input
                id="coupon-limit"
                className="awj-input"
                type="number"
                inputMode="numeric"
                min="1"
                step="1"
                value={draft.usageLimit}
                onChange={(event) => set('usageLimit', event.target.value)}
              />
            </div>
          </div>

          <VisibilitySwitch
            label={t('coupons.switchedOn')}
            hint={t('coupons.switchedOnHint')}
            checked={draft.isActive}
            onChange={(isActive) => set('isActive', isActive)}
          />
        </section>

        {error ? (
          <p className="bilingual__error" role="alert">
            {error}
          </p>
        ) : null}

        {/* A code that has been redeemed is named on its orders, so it can only be switched off. */}
        {editing && editing.timesUsed === 0 ? (
          <section className="card card--danger" aria-labelledby="coupon-delete">
            <h2 className="section-label" id="coupon-delete">
              {t('coupons.delete')}
            </h2>
            <p className="field__hint">{t('coupons.deleteHint')}</p>
            <Button
              type="button"
              variant="secondary"
              className="awj-button--quietDanger"
              size="sm"
              onClick={() => setConfirmDelete(true)}
            >
              {t('common.delete')}
            </Button>
          </section>
        ) : null}
      </div>

      <Dialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title={t('coupons.deleteTitle', { code: editing?.code ?? '' })}
        closeLabel={t('common.close')}
        size="sm"
        footer={
          <>
            <Button type="button" variant="ghost" onClick={() => setConfirmDelete(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="button" variant="danger" loading={remove.isPending} onClick={deleteCoupon}>
              {t('common.delete')}
            </Button>
          </>
        }
      >
        <p className="dialog-lede">{t('coupons.deleteHint')}</p>
      </Dialog>

      {unsaved.dialog}
    </FormPage>
  );
}
